import {
  Badge,
  Box,
  Button,
  ComboboxItem,
  Flex,
  Grid,
  Loader,
  LoadingOverlay,
  MultiSelect,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
  useDebouncedState,
  useDebouncedValue,
  useDisclosure,
  useHotkeys,
} from "@mantine/hooks";
import {
  IconBarcode,
  IconBrandBootstrap,
  IconBuilding,
  IconBuildingSkyscraper,
  IconCalendar,
  IconFileTypeCsv,
  IconSearch,
  IconTruck,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useDeferredValue, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  repositoryDelivery,
  repositoryPos,
  repositoryMdm,
} from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { changeSearchValue } from "../../../_base/helper/FunctionHelper";
import { _localization } from "../../../config/location";
import { MessageResponse } from "../../../model/MessageResponse";
import {
  getMasterOrgSelect,
  getTblInventoryTransactionTypeOutWard,
} from "../../../service/getSelectApi";
import { BASE_API_DELIVERY } from "../../../config";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import dayjs from "dayjs";

interface IStorageReport {
  ttmv?: string | null;
  ghiChu?: string | null;
  ngayNhapNcc?: string | null; // ISO date string
  maVt: string;
  tenVt: string;
  maVach?: string | null;
  idCn: number;
  maCn: string;
  tenCn: string;
  idKho: string | number;
  maKho: string;
  tenKho: string;
  luuKhoMuaHang?: number | null;
  ngayNhap?: string | null; // ISO date string
  loaiPhieuNhap?: string | null;
  soNgayLuuKhoDc?: number | null;
  ngayXuat?: string | null; // ISO date string or null
  loaiPhieuXuat?: string | null;
  maNcc?: string | null;
  soPhieuNhapNcc?: string | null;
  giaMua?: number | null;
  maNhvt?: string | null;
  tenNhvt?: string | null;
}

type ColumnPinningType = {
  left?: string[];
  right?: string[];
};

const StorageReport = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<IStorageReport[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const rowSelectionDeferred =
    useDeferredValue<MRT_RowSelectionState>(rowSelection);
  const [selectIds, setSelectIds] = useState<string[]>([]);
  const [visible, { toggle, open, close }] = useDisclosure(false);

  const location = useLocation();
  const _searchJson = sessionStorage.getItem(String(location.pathname))
    ? JSON.parse(sessionStorage.getItem(String(location.pathname)) ?? "")
    : null;
  const checkedPinSearch = localStorage.getItem("checkedPinSearch") == "true";
  const [search, setSearch] = useState<any>({
    maVts: _searchJson && checkedPinSearch ? _searchJson.maVts : [],
    idCns: _searchJson && checkedPinSearch ? _searchJson.idCns : [],
    idKhos: _searchJson && checkedPinSearch ? _searchJson.idKhos : [],
    maNhvts: _searchJson && checkedPinSearch ? _searchJson.maNhvts : [],
    loaiPhieuXuats:
      _searchJson && checkedPinSearch ? _searchJson.loaiPhieuXuats : [],
    maNccs: _searchJson && checkedPinSearch ? _searchJson.maNccs : [],
    ngayNhapNccTu:
      _searchJson && checkedPinSearch ? _searchJson.ngayNhapNccTu : null,
    ngayNhapNccDen:
      _searchJson && checkedPinSearch ? _searchJson.ngayNhapNccDen : null,
    ngayNhapTu: _searchJson && checkedPinSearch && _searchJson.ngayNhapTu ? new Date(_searchJson.ngayNhapTu) : dayjs().subtract(1, 'month').toDate(),
    ngayNhapDen:
      _searchJson && checkedPinSearch && _searchJson.ngayNhapDen ? new Date(_searchJson.ngayNhapDen) : dayjs().toDate(),
    ngayXuatTu: _searchJson && checkedPinSearch ? _searchJson.ngayXuatTu : null,
    ngayXuatDen:
      _searchJson && checkedPinSearch ? _searchJson.ngayXuatDen : null,
  });
  const [keySearch, setKeySearch] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.keySearch : "",
    300
  );

  const [searchItem, setSearchItem] = useState<string>("");
  const [debouncedSearchItem] = useDebouncedValue<string>(searchItem, 300);
  const [dataItem, setDataItem] = useState<ComboboxItem[]>([]);
  const [dataMasterOrg, setDataMasterOrg] = useState<ComboboxItem[]>([]);
  const [dataNhvt, setDataNhvt] = useState<ComboboxItem[]>([]);
  const [dataVendorSelect, setDataVendorSelect] = useState<ComboboxItem[]>([]);
  const [dataInventoryTransactionType, setDataInventoryTransactionType] =
    useState<ComboboxItem[]>([]);
  const [dataInvCode, setDataInvCode] = useState<ComboboxItem[]>([]);
  const [dataLoaiPhieuXuat, setDataLoaiPhieuXuat] = useState<ComboboxItem[]>([
    {
      label: "XUẤT_DIEU_CHUYEN_CHI_NHANH",
      value: "XUAT_DIEU_CHUYEN_CHI_NHANH",
    },
    { label: "XUẤT_DIEU_CHUYEN_NOI_BO", value: "XUAT_DIEU_CHUYEN_NOI_BO" },
  ]);

  const columns = React.useMemo<MRT_ColumnDef<IStorageReport>[]>(() => [
    {
      accessorKey: "ttmv",
      header: "TTMV",
      Cell: ({ renderedCellValue }) => (
        <Text size="12.5px">{renderedCellValue ?? ""}</Text>
      ),
    },
    {
      accessorKey: "maVt",
      header: "Mã vật tư",
      Cell: ({ renderedCellValue }) => (
        <Badge variant="dot" color="green">
          {renderedCellValue}
        </Badge>
      ),
    },
    { accessorKey: "tenVt", header: "Tên vật tư" },
    { accessorKey: "maVach", header: "Mã vạch" },
    // {
    //   accessorKey: "idCn",
    //   header: "ID CN",
    //   enableColumnOrdering: false,
    //   Cell: ({ cell }) => <Text>{cell.getValue<number>()}</Text>,
    // },
    {
      accessorKey: "maCn",
      header: "Mã chi nhánh",
    },
    {
      accessorKey: "tenCn",
      header: "Chi nhánh",
    },
    // {
    //   accessorKey: "idKho",
    //   header: "ID Kho",
    //   Cell: ({ cell }) => <Text>{String(cell.getValue<any>() ?? "")}</Text>,
    // },
    {
      accessorKey: "maKho",
      header: "Mã kho",
    },
    {
      accessorKey: "tenKho",
      header: "Tên kho",
    },
    {
      accessorKey: "luuKhoMuaHang",
      header: "Lưu kho mua hàng",
      Cell: ({ cell }) =>
        cell.getValue<number | null>() != null
          ? cell.getValue<number | null>()!.toString()
          : "",
    },
    {
      accessorKey: "ngayNhapNcc",
      header: "Ngày nhập NCC",
      Cell: ({ cell }) => {
        const v = cell.getValue<string | null>();
        return v ? new Date(v).toLocaleDateString("vi-VN") : "";
      },
    },
    {
      accessorKey: "ngayNhap",
      header: "Ngày nhập",
      Cell: ({ cell }) => {
        const v = cell.getValue<string | null>();
        return v ? new Date(v).toLocaleString("vi-VN") : "";
      },
    },
    {
      accessorKey: "loaiPhieuNhap",
      header: "Loại phiếu nhập",
    },
    {
      accessorKey: "soNgayLuuKhoDc",
      header: "Số ngày lưu kho",
      Cell: ({ cell }) =>
        cell.getValue<number | null>() != null
          ? String(cell.getValue<number | null>())
          : "",
    },
    {
      accessorKey: "ngayXuat",
      header: "Ngày xuất",
      Cell: ({ cell }) => {
        const v = cell.getValue<string | null>();
        return v ? new Date(v).toLocaleDateString("vi-VN") : "";
      },
    },
    { accessorKey: "loaiPhieuXuat", header: "Loại phiếu xuất" },
    { accessorKey: "maNcc", header: "Mã NCC" },
    { accessorKey: "soPhieuNhapNcc", header: "Số phiếu NCC" },
    {
      accessorKey: "giaMua",
      header: "Giá mua",
      Cell: ({ cell }) =>
        cell.getValue<number | null>() != null
          ? Number(cell.getValue<number | null>()).toLocaleString()
          : "",
    },
    { accessorKey: "maNhvt", header: "Mã nhóm VT" },
    { accessorKey: "tenNhvt", header: "Nhóm vật tư" },
    { accessorKey: "ghiChu", header: "Ghi chú" },
  ], []);

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);
    try {
      let url = `/api/v1/MvBcLuuKho/get-list?Skip=${pagination.pageIndex * pagination.pageSize
        }&Take=${pagination.pageSize}`;

      if (keySearch && keySearch.length > 0) {
        url += `&KeySearch=${keySearch.trim()}`;
      }

      // Add array filters
      if (search.maVts && search.maVts.length > 0) {
        search.maVts.forEach((maVt: string) => {
          url += `&MaVts=${maVt}`;
        });
      }
      if (search.idCns && search.idCns.length > 0) {
        search.idCns.forEach((idCn: string) => {
          url += `&IdCns=${idCn}`;
        });
      }
      if (search.idKhos && search.idKhos.length > 0) {
        search.idKhos.forEach((idKho: string) => {
          url += `&IdKhos=${idKho}`;
        });
      }
      if (search.maNhvts && search.maNhvts.length > 0) {
        search.maNhvts.forEach((maNhvt: string) => {
          url += `&MaNhvts=${maNhvt}`;
        });
      }
      if (search.loaiPhieuXuats && search.loaiPhieuXuats.length > 0) {
        search.loaiPhieuXuats.forEach((loaiPhieuXuat: string) => {
          url += `&LoaiPhieuXuats=${loaiPhieuXuat}`;
        });
      }
      if (search.maNccs && search.maNccs.length > 0) {
        search.maNccs.forEach((maNcc: string) => {
          url += `&MaNccs=${maNcc}`;
        });
      }
      // Add date filters
      if (search.ngayNhapNccTu) {
        url += `&NgayNhapNccTu=${search.ngayNhapNccTu.toISOString()}`;
      }
      if (search.ngayNhapNccDen) {
        url += `&NgayNhapNccDen=${search.ngayNhapNccDen.toISOString()}`;
      }
      if (search.ngayNhapTu) {
        url += `&NgayNhapTu=${search.ngayNhapTu.toISOString()}`;
      }
      if (search.ngayNhapDen) {
        url += `&NgayNhapDen=${search.ngayNhapDen.toISOString()}`;
      }
      if (search.ngayXuatTu) {
        url += `&NgayXuatTu=${search.ngayXuatTu.toISOString()}`;
      }
      if (search.ngayXuatDen) {
        url += `&NgayXuatDen=${search.ngayXuatDen.toISOString()}`;
      }

      const res = await repositoryDelivery.get<
        MessageResponse<IStorageReport[]>
      >(url);

      if (res && res.success) {
        const result = res.data;
        setData(result);
        setRowCount(res.totalCount);
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
      sessionStorage.setItem(
        String(location.pathname),
        JSON.stringify({
          ...search,
          keySearch: keySearch,
        })
      );
    }
  };

  const exportDataExcel = async () => {
    open();
    try {
      const payload: any = {
        KeySearch: (keySearch || "").trim(),
        MaVts:
          Array.isArray(search.maVts) && search.maVts.length > 0
            ? search.maVts
            : null,
        IdCns:
          Array.isArray(search.idCns) && search.idCns.length > 0
            ? search.idCns.map((v: any) => (v === null ? null : Number(v)))
            : null,
        IdKhos:
          Array.isArray(search.idKhos) && search.idKhos.length > 0
            ? search.idKhos.map((v: any) => (v === null ? null : Number(v)))
            : null,
        MaNhvts:
          Array.isArray(search.maNhvts) && search.maNhvts.length > 0
            ? search.maNhvts
            : null,
        LoaiPhieuXuats:
          Array.isArray(search.loaiPhieuXuats) &&
            search.loaiPhieuXuats.length > 0
            ? search.loaiPhieuXuats
            : null,
        MaNccs:
          Array.isArray(search.maNccs) && search.maNccs.length > 0
            ? search.maNccs
            : null,
        NgayNhapNccTu: search.ngayNhapNccTu
          ? new Date(search.ngayNhapNccTu).toISOString()
          : null,
        NgayNhapNccDen: search.ngayNhapNccDen
          ? new Date(search.ngayNhapNccDen).toISOString()
          : null,
        NgayNhapTu: search.ngayNhapTu
          ? new Date(search.ngayNhapTu).toISOString()
          : null,
        NgayNhapDen: search.ngayNhapDen
          ? new Date(search.ngayNhapDen).toISOString()
          : null,
        NgayXuatTu: search.ngayXuatTu
          ? new Date(search.ngayXuatTu).toISOString()
          : null,
        NgayXuatDen: search.ngayXuatDen
          ? new Date(search.ngayXuatDen).toISOString()
          : null,
        Sort: null,
        IsOrder: null,
        Email: null,
      };

      const _url = BASE_API_DELIVERY + "/api/v1/MvBcLuuKho/export";
      const token = localStorage.getItem("token");

      const response = await fetch(_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token
            ?.replace(/\"/g, "")
            .replace(/\"/g, "")}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 200) {
        NotificationExtension.Success("Yêu cầu xuất đã vào hàng đợi.");
      } else {
        // read server error text if available
        let text = "";
        try {
          text = await response.text();
          console.error("Export failed response text:", text);
        } catch (e) {
          console.error(e);
        }
        NotificationExtension.Fails("Export Excel thất bại !");
      }
    } catch (error) {
      console.log(error);
    } finally {
      close();
    }
  };

  const fetchDataItem = async () => {
    let url = "api/v1/TblItem/get-list?Skip=0&Take=50";

    if (searchItem?.length > 0) {
      url += `&ListItemCode=${searchItem?.toUpperCase()}`;
    }

    const response = await repositoryPos.get<MessageResponse<any>>(url);

    if (response && response.success) {
      const transformedData = response.data
        ?.filter((item: any) => item.itemCode && item.itemName)
        .map((item: any) => ({
          label: item.itemCode + " - " + item.itemName,
          value: item.itemCode,
        }));
      const uniqueData = Array.from(
        new Set(transformedData.map((item: any) => item.value))
      ).map((value) =>
        transformedData.find((item: any) => item.value === value)
      );
      setDataItem(uniqueData);
    }
  };

  const fetchDataMasterOrg = async () => {
    const res = await getMasterOrgSelect();
    if (res) {
      const data = res
        ?.filter((item) => item.text && item.value)
        ?.map((item) => ({
          label: `${item.att1} - ${item.text}`,
          value: String(item.value),
        }));
      setDataMasterOrg(data);
    }
  };

  const fetchDataKho = async () => {
    // fetchDataKho removed — using fetchDataInvCode (inventory API) instead
  };

  const fetchDataNhvt = async () => {
    try {
      const url = "/api/v1/TblCatSegment4d/get-list?Skip=0&Take=100";
      const res = await repositoryPos.get<MessageResponse<any>>(url);

      if (res && res.success) {
        const data = res.data
          ?.filter((item: any) => item.codeString && item.name)
          ?.map((item: any) => ({
            label: `${item.codeString} - ${item.name}`,
            value: item.codeString,
          }));
        setDataNhvt(data);
      }
    } catch (error) {
      console.error("Error fetching warehouse group data:", error);
    }
  };

  const getDataVendorSelect = async () => {
    const getData = await repositoryMdm.get<MessageResponse<any>>(
      "/api/v1/TblVendor/get-all"
    );

    if (getData && getData?.success) {
      const result = getData.data;
      setDataVendorSelect(
        result
          ?.filter(
            (item: any) => item.vendorName != null && item.vendorCode != null
          )
          ?.map((item: any) => ({
            value: item.vendorCode,
            label: `${item.vendorCode} - ${item.vendorName}`,
          }))
      );
    }
  };

  const fetchdataInventoryTransactionType = async () => {
    try {
      const response = await getTblInventoryTransactionTypeOutWard();
      if (response && Array.isArray(response)) {
        setDataInventoryTransactionType(
          response.map((type: any) => ({ value: type.value, label: type.text }))
        );
      }
    } catch (error) {
      console.error("Error fetching inventory transaction types:", error);
    }
  };

  const fetchDataInvCode = async () => {
    try {
      const response = await repositoryPos.get<MessageResponse<any>>(
        `/api/v1/TblDmInventory/get-all?checkAuth=true&select=true`
      );
      if (response && response.success) {
        const transformedData = response.data
          ?.filter((i: any) => i.att1 && i.text && i.att1 !== "-1")
          ?.map((item: any) => ({
            label: item.text?.toString(),
            value: String(item.value),
          }));

        setDataInvCode(transformedData);
      }
    } catch (error) {
      console.error("Error fetching inventory data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination?.pageIndex, pagination?.pageSize]);

  useEffect(() => {
    fetchDataMasterOrg();
    fetchDataNhvt();
    getDataVendorSelect();
    fetchdataInventoryTransactionType();
    fetchDataInvCode();
  }, []);

  useEffect(() => {
    fetchDataItem();
  }, [debouncedSearchItem]);

  const getColumnPinningStorage = () => {
    const columnPinningStorage = localStorage.getItem(
      "columnPinningWeighingReport"
    );
    return columnPinningStorage
      ? JSON.parse(columnPinningStorage)
      : {
        left: ["mrt-row-select", "mrt-row-numbers"],
        right: ["enforcement", "action"],
      }; // Giá trị mặc định
  };

  // Lưu columnPinning vào state
  const [columnPinning, setColumnPinning] = useState<ColumnPinningType>(
    getColumnPinningStorage()
  );
  // Hàm lưu vào localStorage
  useEffect(() => {
    localStorage.setItem(
      "columnPinningWeighingReport",
      JSON.stringify(columnPinning)
    );
  }, [columnPinning]);

  useEffect(() => {
    //   startTransition(() => {
    const valuesList = Object.keys(rowSelectionDeferred);
    setSelectIds(valuesList);
    if (valuesList.length < 1) setSelectIds([]);
    else {
      const valuesList = Object.keys(rowSelectionDeferred);
      setSelectIds(valuesList);
    }
    //  });
  }, [rowSelectionDeferred]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (185 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: false,
    positionToolbarAlertBanner: "bottom",
    enableRowNumbers: true,
    //add custom action buttons to top-left of top toolbar
    enableTopToolbar: false,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    enableRowSelection: true,
    getRowId: (row) => row.ttmv?.toString() || Math.random().toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        // left: ["mrt-row-numbers", "maVt"],
        // right: ["action"],
        ...columnPinning,
      },
      columnVisibility: { id: true, phone: false, fax: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    manualPagination: true,
    onColumnPinningChange: (newState) => {
      setColumnPinning(newState);
    },
    onRowSelectionChange: setRowSelection,
    rowCount: rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontWeight: "500", //conditional styling
        fontSize: "12.5px",
        padding: "5px 15px",
      },
    }),
    state: {
      isLoading,
      pagination: pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      rowSelection,
      columnPinning,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    columnFilterDisplayMode: "popover",
    mantineTableBodyRowProps: ({ row }) => ({
      style: { cursor: "pointer" },
    }),
  });

  useHotkeys([
    [
      "Enter",
      () => {
        fetchData();
      },
    ],
  ]);

  return (
    <>
      <div
        ref={headerRef}
        style={{
          width: "100%",
        }}
      >
        <LoadingOverlay
          visible={visible}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 5 }}
          loaderProps={{
            children: (
              <Flex direction={"column"} align="center" gap="xs">
                <Loader type="dots" size={35}></Loader>
                {/* <Title size={15}> Đang upload tài liệu...</Title> */}
                <Title size={15}> Đang xử lý....</Title>
              </Flex>
            ),
          }}
        />
        <Flex justify={"space-between"} align={"center"} w={"100%"}>
          <_breadcrumb></_breadcrumb>
          <Flex gap={"md"}>
            <Button
              leftSection={<IconFileTypeCsv size={16} />}
              onClick={() => exportDataExcel()}
            >
              Export Excel
            </Button>
          </Flex>
        </Flex>
        <Grid mt={10} w={"100%"}>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Nhập từ khóa tìm kiếm"
              defaultValue={keySearch}
              onChange={(e) => setKeySearch(e.currentTarget.value)}
              leftSection={<IconSearch color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <MultiSelect
              placeholder="Mã vật tư"
              leftSection={<IconBarcode size={20} color="#15aabf" />}
              data={dataItem}
              value={
                search.maVts && search.maVts?.length > 0 ? search.maVts : []
              }
              searchable
              clearable
              onSearchChange={setSearchItem}
              nothingFoundMessage={"Không tìm thấy dữ liệu !"}
              onChange={(e) =>
                changeSearchValue(e ? e : [], "maVts", setSearch)
              }
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <MultiSelect
              placeholder="Chi nhánh"
              leftSection={<IconBuildingSkyscraper color="#15aabf" />}
              data={dataMasterOrg}
              value={
                search?.idCns && search?.idCns.length > 0 ? search?.idCns : []
              }
              searchable
              clearable
              nothingFoundMessage={"Không tìm thấy dữ liệu !"}
              onChange={(e) =>
                changeSearchValue(e ? e : [], "idCns", setSearch)
              }
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <MultiSelect
              placeholder="Kho"
              leftSection={<IconBuilding size={20} color="#15aabf" />}
              data={dataInvCode}
              value={
                search?.idKhos && search?.idKhos.length > 0
                  ? search?.idKhos
                  : []
              }
              searchable
              clearable
              nothingFoundMessage={"Không tìm thấy dữ liệu !"}
              onChange={(e) =>
                changeSearchValue(e ? e : [], "idKhos", setSearch)
              }
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <MultiSelect
              placeholder="Nhóm vật tư"
              leftSection={<IconBrandBootstrap size={20} color="#15aabf" />}
              data={dataNhvt}
              value={
                search.maNhvts && search.maNhvts?.length > 0
                  ? search.maNhvts
                  : []
              }
              searchable
              clearable
              nothingFoundMessage="Không tìm thấy dữ liệu !"
              onChange={(e) => {
                changeSearchValue(e ? e : [], "maNhvts", setSearch);
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <MultiSelect
              placeholder="Loại phiếu xuất"
              leftSection={<IconTruck size={20} color="#15aabf" />}
              data={
                dataInventoryTransactionType &&
                  dataInventoryTransactionType.length > 0
                  ? dataInventoryTransactionType
                  : dataLoaiPhieuXuat
              }
              value={
                search.loaiPhieuXuats && search.loaiPhieuXuats?.length > 0
                  ? search.loaiPhieuXuats
                  : []
              }
              searchable
              clearable
              nothingFoundMessage="Không tìm thấy dữ liệu !"
              onChange={(e) => {
                changeSearchValue(e ? e : [], "loaiPhieuXuats", setSearch);
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <MultiSelect
              placeholder="Nhà cung cấp"
              leftSection={<IconTruck size={20} color="#15aabf" />}
              data={dataVendorSelect}
              value={
                search.maNccs && search.maNccs?.length > 0 ? search.maNccs : []
              }
              searchable
              clearable
              nothingFoundMessage="Không tìm thấy dữ liệu !"
              onChange={(e) => {
                changeSearchValue(e ? e : [], "maNccs", setSearch);
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              placeholder="Ngày nhập NCC từ"
              leftSection={<IconCalendar size={20} color="#15aabf" />}
              value={
                search.ngayNhapNccTu ? new Date(search.ngayNhapNccTu) : null
              }
              locale="vi"
              valueFormat="DD/MM/YYYY"
              onChange={(date) =>
                setSearch((prev: any) => ({ ...prev, ngayNhapNccTu: date }))
              }
              clearable
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              placeholder="Ngày nhập NCC đến"
              leftSection={<IconCalendar size={20} color="#15aabf" />}
              value={
                search.ngayNhapNccDen ? new Date(search.ngayNhapNccDen) : null
              }
              onChange={(date) =>
                setSearch((prev: any) => ({ ...prev, ngayNhapNccDen: date }))
              }
              locale="vi"
              valueFormat="DD/MM/YYYY"
              clearable
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              placeholder="Ngày nhập từ"
              leftSection={<IconCalendar size={20} color="#15aabf" />}
              value={search.ngayNhapTu ? new Date(search.ngayNhapTu) : null}
              onChange={(date) =>
                setSearch((prev: any) => ({ ...prev, ngayNhapTu: date }))
              }
              locale="vi"
              valueFormat="DD/MM/YYYY"
              clearable
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              placeholder="Ngày nhập đến"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              leftSection={<IconCalendar size={20} color="#15aabf" />}
              value={search.ngayNhapDen ? new Date(search.ngayNhapDen) : null}
              onChange={(date) =>
                setSearch((prev: any) => ({ ...prev, ngayNhapDen: date }))
              }
              clearable
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              placeholder="Ngày xuất từ"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              leftSection={<IconCalendar size={20} color="#15aabf" />}
              value={search.ngayXuatTu ? new Date(search.ngayXuatTu) : null}
              onChange={(date) =>
                setSearch((prev: any) => ({ ...prev, ngayXuatTu: date }))
              }
              clearable
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              placeholder="Ngày xuất đến"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              leftSection={<IconCalendar size={20} color="#15aabf" />}
              value={search.ngayXuatDen ? new Date(search.ngayXuatDen) : null}
              onChange={(date) =>
                setSearch((prev: any) => ({ ...prev, ngayXuatDen: date }))
              }
              clearable
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Button
              onClick={() => fetchData()}
              leftSection={<IconSearch size={14} />}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </>
  );
};

export default StorageReport;
